import React, { Component, Fragment } from 'react';
// import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import _ from 'lodash';
import PropTypes from 'prop-types';
import './styles.scss';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';

class Pagination2 extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentPage: 1
    };
  }

  componentDidMount() {
    const pageLimit = typeof this.props.pageLimit === 'number' ? this.props.pageLimit : 10;
    const totalRecords = typeof this.props.totalRecords === 'number' ? this.props.totalRecords : 0;

    // pageNeighbours can be: 0, 1, or 2
    const pageNeighbours =
      typeof this.props.pageNeighbours === 'number' ? Math.max(0, Math.min(this.props.pageNeighbours, 2)) : 0;

    const totalPages = Math.ceil(totalRecords / pageLimit);

    this.setState(
      () => ({
        pageLimit,
        totalRecords,
        pageNeighbours,
        totalPages
      }),
      () => {
        this.gotoPage(1);
      }
    );
  }

  componentDidUpdate(prevProps, prevState) {
    let currentPage = this.state.currentPage;

    if (prevProps.currentPage !== this.props.currentPage) {
      currentPage = this.props.currentPage;
    }

    const pageLimit = typeof this.props.pageLimit === 'number' ? this.props.pageLimit : 10;
    const totalRecords = typeof this.props.totalRecords === 'number' ? this.props.totalRecords : 0;

    // pageNeighbours can be: 0, 1, or 2
    const pageNeighbours =
      typeof this.props.pageNeighbours === 'number' ? Math.max(0, Math.min(this.props.pageNeighbours, 2)) : 0;

    const totalPages = Math.ceil(totalRecords / pageLimit);

    if (
      prevState.currentPage !== currentPage ||
      prevState.pageLimit !== pageLimit ||
      prevState.totalRecords !== totalRecords ||
      prevState.pageNeighbours !== pageNeighbours ||
      prevState.totalPages !== totalPages
    ) {
      this.setState(() => ({
        currentPage,
        pageLimit,
        totalRecords,
        pageNeighbours,
        totalPages
      }));
    }
  }

  /**
   * Let's say we have 10 pages and we set pageNeighbours to 2
   * Given that the current page is 6
   * The pagination control will look like the following:
   *
   * (1) < {4 5} [6] {7 8} > (10)
   *
   * (x) => terminal pages: first and last page (always visible)
   * [x] => represents current page
   * {...x} => represents page neighbours
   */
  fetchPageNumbers = () => {
    const totalPages = this.state.totalPages;
    const currentPage = this.state.currentPage;
    const pageNeighbours = this.state.pageNeighbours;

    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = this.state.pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);

      let pages = _.range(startPage, endPage + 1);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = _.range(startPage - spillOffset, startPage - 1 + 1);
          pages = [LEFT_PAGE, ...extraPages, ...pages];
          break;
        }

        // handle: (1) {2 3} [4] {5 6} > {10}
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = _.range(endPage + 1, endPage + spillOffset + 1);
          pages = [...pages, ...extraPages, RIGHT_PAGE];
          break;
        }

        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
          break;
        }
      }

      return [1, ...pages, totalPages];
    }

    return _.range(1, totalPages + 1);
  };

  gotoPage = page => {
    const { onPageChanged = f => f } = this.props;

    const currentPage = Math.max(1, Math.min(page, this.state.totalPages));

    const paginationData = {
      currentPage,
      totalPages: this.state.totalPages,
      pageLimit: this.state.pageLimit,
      totalRecords: this.state.totalRecords
    };

    this.setState({ currentPage }, () => onPageChanged(paginationData));
  };

  handleClick = page => evt => {
    evt.preventDefault();
    this.gotoPage(page);
  };

  handleMoveLeft = evt => {
    evt.preventDefault();
    this.gotoPage(this.state.currentPage - this.state.pageNeighbours * 2 - 1);
  };

  handleMoveRight = evt => {
    evt.preventDefault();
    this.gotoPage(this.state.currentPage + this.state.pageNeighbours * 2 + 1);
  };

  render() {
    if (!this.state.totalRecords || this.state.totalPages === 1) {
      return null;
    }

    const { currentPage } = this.state;
    const pages = this.fetchPageNumbers();

    return (
      <Fragment>
        <nav aria-label="Pagination">
          <ul className="pagination">
            {pages.map((page, index) => {
              if (page === LEFT_PAGE) {
                return (
                  <li key={index} className="page-item">
                    <a href="#" className="page-link" aria-label="Previous" onClick={this.handleMoveLeft}>
                      <span aria-hidden="true">&laquo;</span>
                      <span className="sr-only">Previous</span>
                    </a>
                  </li>
                );
              }

              if (page === RIGHT_PAGE) {
                return (
                  <li key={index} className="page-item">
                    <a href="#" className="page-link" aria-label="Previous" onClick={this.handleMoveRight}>
                      <span aria-hidden="true">&raquo;</span>
                      <span className="sr-only">Next</span>
                    </a>
                  </li>
                );
              }

              return (
                <li key={index} className={`page-item${currentPage === page ? ' active' : ''}`}>
                  <a className="page-link" href="#" onClick={this.handleClick(page)}>
                    {page}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>
      </Fragment>
    );
  }
}

Pagination2.propTypes = {
  totalRecords: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  pageLimit: PropTypes.number,
  pageNeighbours: PropTypes.number,
  onPageChanged: PropTypes.func
};

export default Pagination2;
